import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import TreeNode from 'primereact/treenode';
import { TreeTable, TreeTableExpandedKeysType } from 'primereact/treetable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  EpTreeNode,
  PatchEpPositionsTagsRequest,
  collectDescendantPositionIds,
  collectExpandedKeys,
  buildEpTaggingIndex,
  filterEpTreeForTagging
} from '../../../Helper/ApiHelper/LvEpNetworkHelper';
import {
  EMPTY_FIELD_PLACEHOLDER,
  tags as tagLabelMap
} from '../../../Helper/Statics/Constants';
import BeeButton from '../../Atoms/BeeButton';
import BeeDropDown from '../../Atoms/BeeDropDown';
import BeeLoadingSpinner from '../../Atoms/BeeLoadingSpinner';
import BeeSearchBar from '../../Atoms/BeeSearchBar';
import AlphaBulkTagDialog from './AlphaBulkTagDialog';
import './AlphaEpCatalogTaggingDialog.scss';
import BeeOutlinedButton from '../../Atoms/BeeOutlinedButton';

export type TagOption = { label: string; value: string };
const TAG_FILTER_UNTAGGED = '__untagged';
type TagFilterOption = { label: string; value: string | null };
/** Nur vom Nutzer gesetzte Zuordnungen (Position-ID → TagID). */
type EpPositionTagDraft = Record<string, string | null>;

type AlphaEpCatalogTaggingDialogProps = {
  visible: boolean;
  header?: string;
  readOnly: boolean;
  epCatalog?: EpTreeNode[] | null;
  tags?: string[];
  isLoading?: boolean;
  onHide: () => void;
  onConfirm?: (payload: PatchEpPositionsTagsRequest) => void;
};

function buildPatchEpPositionsRequest(
  draft: EpPositionTagDraft
): PatchEpPositionsTagsRequest {
  const values: Record<string, string> = {};
  Object.entries(draft)
    .filter(([, tag]) => tag != null && String(tag).trim() !== '')
    .forEach(([posId, tag]) => {
      values[posId] = String(tag).trim();
    });
  return { values };
}

export default function AlphaEpCatalogTaggingDialog({
  visible,
  header = 'EP-Katalog zu Leistungsclustern zuordnen',
  readOnly = false,
  epCatalog,
  tags = [],
  isLoading = false,
  onHide,
  onConfirm
}: AlphaEpCatalogTaggingDialogProps) {
  const [expanded, setExpanded] = useState<TreeTableExpandedKeysType>();
  const [tagByPositionId, setTagByPositionId] = useState<EpPositionTagDraft>(
    {}
  );
  const [treeSearch, setTreeSearch] = useState('');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [bulkTagVisible, setBulkTagVisible] = useState(false);
  const [bulkTagCategory, setBulkTagCategory] = useState<EpTreeNode | null>(
    null
  );

  const getEffectiveTagForNode = useCallback(
    (node: EpTreeNode): string | null => {
      const draft = tagByPositionId[node.key];
      if (draft !== undefined) {
        const trimmed = draft == null ? '' : String(draft).trim();
        return trimmed === '' ? null : trimmed;
      }
      const initialTag = (node.data.tags ?? []).find((t) => tags.includes(t));
      return initialTag ?? null;
    },
    [tagByPositionId, tags]
  );

  const filteredEpCatalog = useMemo(() => {
    const shouldFilterByTag = tagFilter != null;
    const positionPredicate = shouldFilterByTag
      ? (node: EpTreeNode) => {
          const effectiveTag = getEffectiveTagForNode(node);
          if (tagFilter === TAG_FILTER_UNTAGGED) {
            return effectiveTag == null;
          }
          return effectiveTag === tagFilter;
        }
      : undefined;
    return filterEpTreeForTagging(
      epCatalog ?? undefined,
      treeSearch,
      positionPredicate
    );
  }, [epCatalog, treeSearch, tagFilter, getEffectiveTagForNode]);
  const hasFilterActive = (treeSearch ?? '').trim() !== '' || tagFilter != null;
  const treeEmptyMessage =
    (epCatalog?.length ?? 0) > 0 && filteredEpCatalog.length === 0
      ? hasFilterActive
        ? 'Keine Treffer für Suche oder Filter'
        : 'Keine Positionen gefunden'
      : 'Keine Positionen gefunden';
  const tagOptions: TagOption[] = useMemo(
    () =>
      tags.map((key) => ({
        label:
          (tagLabelMap as Record<string, string>)[key] != null
            ? (tagLabelMap as Record<string, string>)[key]
            : key,
        value: key
      })),
    [tags]
  );
  const tagFilterOptions: TagFilterOption[] = useMemo(() => {
    return [
      { label: 'Alle Positionen', value: null },
      { label: 'Ohne Tag', value: TAG_FILTER_UNTAGGED },
      ...tagOptions
    ];
  }, [tagOptions]);

  const taggingIndex = useMemo(
    () => buildEpTaggingIndex(epCatalog ?? undefined, tags),
    [epCatalog, tags]
  );

  const initialCategoryTaggedLeaves = useMemo(() => {
    const result: Record<string, number> = {};
    Object.entries(taggingIndex.initialHasTag).forEach(
      ([positionId, hasTag]) => {
        if (!hasTag) {
          return;
        }
        const ancestors = taggingIndex.positionAncestors[positionId] ?? [];
        ancestors.forEach((categoryId) => {
          result[categoryId] = (result[categoryId] ?? 0) + 1;
        });
      }
    );
    return result;
  }, [taggingIndex]);

  const hasChanges = useMemo(
    () => Object.keys(tagByPositionId).length > 0,
    [tagByPositionId]
  );

  const categoryTaggedLeaves = useMemo(() => {
    const nextCounts: Record<string, number> = {
      ...initialCategoryTaggedLeaves
    };
    Object.entries(tagByPositionId).forEach(([positionId, tag]) => {
      const prevHasTag = taggingIndex.initialHasTag[positionId] ?? false;
      const trimmed = tag == null ? '' : String(tag).trim();
      const nextHasTag = trimmed !== '' && tags.includes(trimmed);
      if (prevHasTag === nextHasTag) {
        return;
      }
      const delta = nextHasTag ? 1 : -1;
      /* update category tagged leaves */
      const ancestors = taggingIndex.positionAncestors[positionId] ?? [];
      ancestors.forEach((categoryId) => {
        const current = nextCounts[categoryId] ?? 0;
        nextCounts[categoryId] = Math.max(0, current + delta);
      });
    });
    return nextCounts;
  }, [
    initialCategoryTaggedLeaves,
    tagByPositionId,
    tags,
    taggingIndex.initialHasTag,
    taggingIndex.positionAncestors
  ]);

  const bulkAffectedPositionIds = useMemo(() => {
    if (!bulkTagCategory || !epCatalog) {
      return [];
    }
    return collectDescendantPositionIds(epCatalog, bulkTagCategory.key);
  }, [bulkTagCategory, epCatalog]);

  useEffect(() => {
    if (visible && filteredEpCatalog.length > 0) {
      // Expand only depth 1 and 2 initially for better overview.
      setExpanded(collectExpandedKeys(filteredEpCatalog, 2));
    } else if (visible) {
      setExpanded(undefined);
    }
  }, [visible, filteredEpCatalog]);

  function handleHide() {
    onHide();
  }

  function updateTaggingForPositions(
    positionIds: string[],
    selected: string | null
  ) {
    if (positionIds.length === 0) {
      return;
    }
    setTagByPositionId((prev) => {
      const next = { ...prev };
      positionIds.forEach((positionId) => {
        next[positionId] = selected;
      });
      return next;
    });
  }

  function handleTagChange(positionId: string, selected: string | null) {
    updateTaggingForPositions([positionId], selected);
  }

  async function handleConfirm() {
    if (!onConfirm) {
      return;
    }
    const payload = buildPatchEpPositionsRequest(tagByPositionId);
    try {
      setIsSaving(true);
      onConfirm(payload);
    } catch {
      // Parent shows errors; dialog stays open.
    } finally {
      setIsSaving(false);
    }
  }

  function openBulkTagDialogForNode(node: EpTreeNode) {
    if (readOnly || tagOptions.length === 0) {
      return;
    }
    setBulkTagCategory(node);
    setBulkTagVisible(true);
  }

  function closeBulkTagDialog() {
    setBulkTagVisible(false);
    setBulkTagCategory(null);
  }

  function applyBulkTag(option: TagOption | null) {
    if (!bulkTagCategory || !option || !epCatalog) {
      closeBulkTagDialog();
      return;
    }
    const ids = collectDescendantPositionIds(epCatalog, bulkTagCategory.key);
    if (ids.length === 0) {
      closeBulkTagDialog();
      return;
    }
    const bulkTagValue = option.value;
    updateTaggingForPositions(ids, bulkTagValue);
    closeBulkTagDialog();
  }

  function rowClassForEpTree(node: TreeNode) {
    const d = (node as unknown as EpTreeNode).data;
    return {
      'priceInput-row-category-tabIndex': d?.type === 'category',
      'priceInput-row-position-noTabIndex': d?.type === 'position'
    };
  }

  function renderTitleCell(node: TreeNode) {
    const d = (node as unknown as EpTreeNode).data;
    if (d.type === 'position') {
      return (
        <span className={''}>
          <i className={'pi pi-file '}></i>{' '}
          {d.number && (
            <span className={'text-sm text-color-secondary'}>[{d.number}]</span>
          )}{' '}
          <span>{d.title ?? d.shortTitle ?? '—'}</span>
        </span>
      );
    }
    return (
      <span>
        <i className={'pi pi-folder '}></i>{' '}
        {d.title ?? d.shortTitle ?? 'Kategorie'}
      </span>
    );
  }

  function renderCompletenessCell(node: TreeNode) {
    const row = node as unknown as EpTreeNode;
    const d = row.data;
    if (d.type !== 'category') {
      return null;
    }
    const totalLeaves = taggingIndex.categoryTotalLeaves[row.key] ?? 0;
    if (totalLeaves === 0) {
      return <span>{EMPTY_FIELD_PLACEHOLDER}</span>;
    }
    const taggedLeaves = categoryTaggedLeaves[row.key] ?? 0;
    const isComplete = taggedLeaves === totalLeaves;
    return (
      <span
        className={!isComplete ? '' : 'valid-color font-semibold'}
      >{`${taggedLeaves}/${totalLeaves}`}</span>
    );
  }

  function renderTagCell(node: TreeNode) {
    const row = node as unknown as EpTreeNode;
    const d = row.data;
    if (d.type !== 'position') {
      if (readOnly || tagOptions.length === 0) {
        return null;
      }
      return (
        <BeeOutlinedButton
          label={'Kategorie zuordnen'}
          disabled={readOnly || tagOptions.length === 0}
          type={'secondary'}
          onClick={() => openBulkTagDialogForNode(row)}
        />
      );
    }

    const positionId = row.key;
    const draftTag = tagByPositionId[positionId];
    const initialTag = (d.tags ?? []).find((t) => tags.includes(t)) ?? null;
    const currentTag = draftTag !== undefined ? draftTag : initialTag;

    if (tagOptions.length === 0) {
      return (
        <span className={'text-sm text-color-secondary'}>
          Keine Tags konfiguriert
        </span>
      );
    }
    return (
      <BeeDropDown
        value={currentTag}
        options={tagOptions}
        optionLabel={'label'}
        readOnly={readOnly}
        disabled={false}
        onChange={(item: string | null) =>
          handleTagChange(positionId, item ?? null)
        }
      />
    );
  }

  function injectBulkTagDialog() {
    if (!bulkTagVisible || !bulkTagCategory) {
      return null;
    }
    const affectedCount = bulkAffectedPositionIds.length;
    const categoryTitle =
      bulkTagCategory.data.title ??
      bulkTagCategory.data.shortTitle ??
      'Kategorie';
    return (
      <AlphaBulkTagDialog
        visible={bulkTagVisible}
        categoryTitle={categoryTitle}
        affectedCount={affectedCount}
        tagOptions={tagOptions}
        disabled={tagOptions.length === 0}
        onCancel={closeBulkTagDialog}
        onConfirm={applyBulkTag}
      />
    );
  }

  function injectTreeBody() {
    if (isLoading) {
      return (
        <div className={'flex justify-content-center p-4'}>
          <BeeLoadingSpinner strokeWidth={'3'} type={'secondary'} />
        </div>
      );
    }
    return (
      <TreeTable
        value={filteredEpCatalog as unknown as TreeNode[]}
        expandedKeys={expanded}
        onToggle={(e) => setExpanded(e.value)}
        rowClassName={(rowData: TreeNode) => rowClassForEpTree(rowData)}
        stripedRows
        className={'ep-catalog-tagging-table'}
        emptyMessage={treeEmptyMessage}
      >
        <Column
          header={'Bezeichnung'}
          className={'ep-catalog-tagging-table__title-col'}
          expander
          body={(node: TreeNode) => renderTitleCell(node)}
        />
        <Column
          header={'Leistungscluster'}
          className={'ep-catalog-tagging-table__tag-col'}
          body={(node: TreeNode) => renderTagCell(node)}
        />
        <Column
          header={'Vollständigkeit'}
          className={'ep-catalog-tagging-table__completeness-col'}
          body={(node: TreeNode) => renderCompletenessCell(node)}
        />
      </TreeTable>
    );
  }

  function injectFooter() {
    return (
      <div className={'flex justify-content-end gap-2'}>
        {readOnly ? (
          <BeeButton
            type={'primary'}
            label={'Schließen'}
            onClick={() => handleHide()}
          />
        ) : (
          <>
            <BeeButton
              type={'secondary'}
              label={'Abbrechen'}
              onClick={() => handleHide()}
            />
            <BeeButton
              type={'primary'}
              label={'Speichern'}
              disabled={isLoading || isSaving || !onConfirm || !hasChanges}
              onClick={() => void handleConfirm()}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <Dialog
      header={header}
      visible={visible}
      modal={true}
      footer={injectFooter}
      className={'bee-dialog-large bee-dialog-primary'}
      onHide={() => handleHide()}
    >
      <div className={'mb-3'}>
        <p className={'mb-2'}>
          Ordnen Sie jeder Position einen Leistungscluster (Tag) zu.
        </p>
        <div className={'flex flex-wrap gap-3 align-items-end'}>
          <div className={'flex-auto '}>
            <BeeDropDown
              label={'Filter'}
              value={tagFilter}
              options={tagFilterOptions}
              optionLabel={'label'}
              readOnly={false}
              disabled={isLoading}
              onChange={(value: string | null) => setTagFilter(value)}
            />
          </div>
          <div className={'flex-1 '}>
            <BeeSearchBar
              value={treeSearch}
              disabled={isLoading}
              onSearch={(term) => setTreeSearch(term)}
            />
          </div>
        </div>
      </div>
      <div className={'mb-3'}>{injectTreeBody()}</div>
      {bulkTagVisible && injectBulkTagDialog()}
    </Dialog>
  );
}
